import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Button from '../ui/Button';
import Tooltip from '../ui/Tooltip';
import { getMenuItems } from '../../config/dashboardMenus';
import { useRoleAccess } from '../../hooks/useRoleAccess';
import { useMobileResponsive, useSwipeGesture } from '../../hooks/useMobileResponsive';

const UnifiedDashboardSidebar = ({ mobileOpen, setMobileOpen }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { userRole } = useRoleAccess();
  const { isMobile } = useMobileResponsive();
  
  const [expandedItems, setExpandedItems] = useState({});
  const [companySwitcherOpen, setCompanySwitcherOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  // Swipe gesture handlers for mobile
  const swipeHandlers = useSwipeGesture({
    onSwipeLeft: () => {
      if (isMobile && mobileOpen) {
        setMobileOpen(false);
      }
    },
    onSwipeRight: () => {
      if (isMobile && !mobileOpen) {
        setMobileOpen(true);
      }
    }
  });

  // Get menu items based on user role
  const menuItems = getMenuItems(userRole);

  // Filter menu items based on role permissions
  const filteredMenuItems = menuItems?.filter(item => {
    // All items visible by default unless explicitly restricted
    return true;
  });

  // Close mobile sidebar when route changes
  React.useEffect(() => {
    if (isMobile && mobileOpen) {
      setMobileOpen(false);
    }
  }, [location?.pathname]);

  const sidebarClasses = `fixed left-0 top-16 bottom-0 bg-background border-r border-border z-30 overflow-hidden transition-transform duration-300 ${
    isMobile ? (mobileOpen ? 'translate-x-0' : '-translate-x-full') : ''
  }`;

  const sidebarVariants = {
    expanded: {
      width: 240,
      transition: { type: "spring", stiffness: 300, damping: 30 }
    },
    collapsed: {
      width: 64,
      transition: { type: "spring", stiffness: 300, damping: 30 }
    }
  };

  const contentVariants = {
    expanded: { opacity: 1, transition: { delay: 0.1 } },
    collapsed: { opacity: 0, transition: { duration: 0.1 } }
  };

  const toggleExpanded = (itemId) => {
    setExpandedItems(prev => ({ ...prev, [itemId]: !prev?.[itemId] }));
  };

  const onToggle = () => {
    setCollapsed(prev => !prev);
  };

  const renderMenuItem = (item) => {
    const isExpanded = expandedItems?.[item?.id];

    if (item?.expandable) {
      return (
        <div key={item?.id} className="space-y-1">
          <div className="relative">
            <button
              onClick={() => toggleExpanded(item?.id)}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                collapsed ? 'justify-center' : ''
              } hover:bg-accent/10 hover:text-accent text-muted-foreground`}
            >
              <div className="flex items-center space-x-3">
                <item.icon className="w-5 h-5 flex-shrink-0" />
                <AnimatePresence>
                  {!collapsed && (
                    <motion.span
                      variants={contentVariants}
                      initial="collapsed"
                      animate="expanded"
                      exit="collapsed"
                      className="truncate"
                    >
                      {item?.label}
                    </motion.span>
                  )}
                </AnimatePresence>
              </div>
              <AnimatePresence>
                {!collapsed && (
                  <motion.div
                    variants={contentVariants}
                    initial="collapsed"
                    animate="expanded"
                    exit="collapsed"
                  >
                    <item.chevronIcon className="w-4 h-4" />
                  </motion.div>
                )}
              </AnimatePresence>
            </button>

            {collapsed && (
              <Tooltip content={item?.label} side="right">
                <div className="absolute inset-0" />
              </Tooltip>
            )}
          </div>
          {/* Sub Items */}
          <AnimatePresence>
            {isExpanded && !collapsed && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
                className="ml-6 space-y-1"
              >
                {item?.subItems?.map(subItem => (
                  <NavLink
                    key={subItem?.id}
                    to={subItem?.path}
                    className={({ isActive }) =>
                      `flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${
                        isActive
                          ? 'bg-accent/10 text-accent font-medium' :'text-muted-foreground hover:bg-accent/5 hover:text-accent'
                      }`
                    }
                  >
                    <span>{subItem?.label}</span>
                    {subItem?.badge && (
                      <span className="bg-accent/20 text-accent text-xs px-2 py-0.5 rounded-full font-medium">
                        {subItem?.badge}
                      </span>
                    )}
                  </NavLink>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      );
    }

    return (
      <div key={item?.id} className="relative">
        <NavLink
          to={item?.path}
          end={item?.exact}
          className={({ isActive }) =>
            `flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              collapsed ? 'justify-center' : ''
            } ${
              isActive
                ? 'bg-accent/10 text-accent' :'text-muted-foreground hover:bg-accent/10 hover:text-accent'
            }`
          }
        >
          <div className="flex items-center space-x-3">
            <item.icon className="w-5 h-5 flex-shrink-0" />
            <AnimatePresence>
              {!collapsed && (
                <motion.span
                  variants={contentVariants}
                  initial="collapsed"
                  animate="expanded"
                  exit="collapsed"
                  className="truncate flex-1"
                >
                  {item?.label}
                </motion.span>
              )}
            </AnimatePresence>
          </div>
          
          {item?.badge && !collapsed && (
            <motion.span
              variants={contentVariants}
              initial="collapsed"
              animate="expanded"
              exit="collapsed"
              className="bg-accent/20 text-accent text-xs px-2 py-0.5 rounded-full font-medium"
            >
              {item?.badge}
            </motion.span>
          )}
        </NavLink>
        {collapsed && (
          <Tooltip content={item?.label} side="right">
            <div className="absolute inset-0" />
          </Tooltip>
        )}
      </div>
    );
  };

  const getSwitchToPath = () => {
    return userRole === 'jobSeeker' ? '/employer-dashboard' : '/dashboard';
  };

  const getSwitchToLabel = () => {
    return userRole === 'jobSeeker' ? 'Switch to Employer' : 'Switch to Job Seeker';
  };

  return (
    <aside className={sidebarClasses}>
      <div className="h-full flex flex-col">
        {/* Toggle Button */}
        <div className="flex items-center justify-end p-3 border-b border-border">
          <Button
            variant="ghost"
            size="icon"
            onClick={onToggle}
            className="h-8 w-8"
          >
            <motion.div
              animate={{ rotate: collapsed ? 0 : 180 }}
              transition={{ duration: 0.2 }}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </motion.div>
          </Button>
        </div>

        {/* Navigation Menu - Use filtered items */}
        <nav className="flex-1 px-4 py-6 overflow-y-auto">
          <ul className="space-y-1">
            {filteredMenuItems?.map((item) => (
              <li key={item?.id}>
                {renderMenuItem(item)}
              </li>
            ))}
          </ul>
        </nav>

        {/* Role Switch Section */}
        <div className="border-t border-border p-4 space-y-2">
          <div className="relative">
            <button
              onClick={() => navigate(getSwitchToPath())}
              className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                collapsed ? 'justify-center' : ''
              } text-muted-foreground hover:bg-accent/10 hover:text-accent`}
            >
              <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m0-4l4-4" />
              </svg>
              <AnimatePresence>
                {!collapsed && (
                  <motion.span
                    variants={contentVariants}
                    initial="collapsed"
                    animate="expanded"
                    exit="collapsed"
                    className="truncate flex-1 text-left"
                  >
                    {getSwitchToLabel()}
                  </motion.span>
                )}
              </AnimatePresence>
            </button>

            {collapsed && (
              <Tooltip content={getSwitchToLabel()} side="right">
                <div className="absolute inset-0" />
              </Tooltip>
            )}
          </div>
        </div>
      </div>
    </aside>
  );
};

export default UnifiedDashboardSidebar;